import React, { useMemo, useState } from 'react';
import { Sparkles, Plus, Check, Loader2, ChevronDown } from 'lucide-react';
import { resolveProposalAddOns } from '../../lib/proposalAddOnVisibility';
import { toast } from 'sonner';
import { formatMoney } from '../../lib/formatMoney';

const API = process.env.REACT_APP_BACKEND_URL;
// The app's shared formatter — the local one here dropped cents, so a client
// could be shown "$1,650" for $1,650.40. Cents are money.
const money = (n) => formatMoney(n);
const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

// GROUP vs VARIANT. Rows used to read
// "DSLR Photo Booth Experience (pro quality) — Roaming Photo Booth (DSLR pro
// quality)": the group name and the variant name, each repeating most of the
// other. The card carries the group as a small eyebrow and the variant as the
// title, with any words the variant repeats from the group stripped out. If
// nothing distinct is left, the name is shown ONCE.
const words = (s) => String(s || '').toLowerCase().match(/[a-z0-9]+/g) || [];
export function splitGroupVariant(groupName, variantName) {
  const group = String(groupName || '').trim();
  const variant = String(variantName || '').trim();
  if (!group) return { eyebrow: '', title: variant };
  if (!variant) return { eyebrow: '', title: group };
  const groupWords = new Set(words(group));
  // Strip parentheticals first — they are almost always the repeat.
  const stripped = variant
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .split(/\s+/)
    .filter((w) => {
      const bare = w.toLowerCase().replace(/[^a-z0-9]+/g, '');
      return bare ? !groupWords.has(bare) : false;
    })
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!stripped) return { eyebrow: '', title: group };
  return { eyebrow: group, title: stripped };
}

export default function ContractUpgrades({ token, baseAmount, addOns, liveAddOns, initialSelected, serverAddOnsTotal, serverNewTotal, onTotalsChanged, hiddenKeys = [], featuredKeys = null, shownKeys = [] }) {
  const base = Number(baseAmount || 0);
  const hasBase = base > 0;
  const [selected, setSelected] = useState(() => {
    const map = {};
    (initialSelected || []).forEach((e) => {
      if (e && e.key) map[e.key] = { name: e.name || e.key, amount: Number(e.amount || 0) };
    });
    return map;
  });
  const [busyKey, setBusyKey] = useState(null);
  // Signing comes before shopping: this block sits BELOW the signature panel
  // and stays collapsed until the client asks for it. Selections live in this
  // component's state, so collapsing never loses one.
  const [open, setOpen] = useState(false);
  // Totals as last returned BY THE SERVER (client-view payload, then the
  // add-on endpoint's response). Never computed here.
  const [serverTotals, setServerTotals] = useState({
    addOnsTotal: serverAddOnsTotal != null ? Number(serverAddOnsTotal) : null,
    newTotal: serverNewTotal != null ? Number(serverNewTotal) : null,
  });

  const rows = useMemo(() => {
    const built = [];
    // ONE definition of what is on offer — see lib/proposalAddOnVisibility.
    // The menu (identity, options, benefits) is LIVE; the price is FROZEN from
    // the snapshot per item/option. Nothing is filtered again here.
    // Row KEYS are deliberately left as they were: they are sent to the
    // package-addon endpoint and matched against stored keys.
    resolveProposalAddOns(liveAddOns, addOns, hiddenKeys, featuredKeys, shownKeys).visible.forEach((it) => {
      const opts = Array.isArray(it.options)
        ? it.options.filter((o) => o && o.name && o.amount != null && Number(o.amount) > 0)
        : [];
      if (opts.length) {
        // Grouped add-on → one flat, signable row per priced option.
        opts.forEach((o) => {
          const parts = splitGroupVariant(it.name, o.name);
          built.push({
            key: slug(it.name) + ':' + slug(o.name),
            name: it.name + ' — ' + o.name,
            eyebrow: parts.eyebrow,
            title: parts.title,
            benefit: o.benefit || '',
            amount: Number(o.amount),
          });
        });
      } else if (it.amount != null && Number(it.amount) > 0) {
        built.push({
          key: slug(it.name),
          name: it.name,
          eyebrow: '',
          title: it.name,
          benefit: it.benefit || '',
          amount: Number(it.amount),
        });
      }
    });
    return built;
  }, [addOns, liveAddOns, hiddenKeys, featuredKeys, shownKeys]);

  const addedKeys = Object.keys(selected);
  const addedItems = Object.values(selected);
  // Anything the client already bought stays visible even if it is no longer
  // on offer — they must be able to see what they added.
  const cards = useMemo(() => {
    const known = new Set(rows.map((r) => r.key));
    const extras = addedKeys
      .filter((k) => !known.has(k))
      .map((k) => ({
        key: k,
        name: selected[k].name,
        eyebrow: '',
        title: selected[k].name,
        benefit: '',
        amount: Number(selected[k].amount || 0),
      }));
    return rows.concat(extras);
  }, [rows, addedKeys, selected]);

  // *** SERVER FIGURES ONLY. *** The browser used to add base + add-ons
  // itself, so the client could be shown a total the server never computed —
  // and then sign it. Both numbers come from the backend: the client-view
  // payload on first render, and the add-on endpoint's own response after
  // each change. The sticky strip below prints THIS number, the same one the
  // signature panel uses.
  const addOnsTotal = serverTotals.addOnsTotal != null
    ? serverTotals.addOnsTotal
    : (serverAddOnsTotal != null ? serverAddOnsTotal : null);
  const newTotal = serverTotals.newTotal != null
    ? serverTotals.newTotal
    : (serverNewTotal != null ? serverNewTotal : null);
  const totalsResolved = newTotal != null;

  const toggleItem = async (a) => {
    if (busyKey) return;
    const removing = a.key in selected;
    setBusyKey(a.key);
    try {
      const res = await fetch(API + '/api/leads/client-view/' + encodeURIComponent(token) + '/package-addon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(removing ? { key: a.key, remove: true } : { key: a.key }),
      });
      if (!res.ok) {
        let d = ''; try { d = (await res.json()).detail || ''; } catch (e) { /* ignore */ }
        throw new Error(d || 'Could not update your package right now.');
      }
      const data = await res.json();
      const nextSel = {};
      (data.items || []).forEach((e) => {
        if (e && e.key) nextSel[e.key] = { name: e.name || e.key, amount: Number(e.amount || 0) };
      });
      setSelected(nextSel);
      // Totals straight from the server's response — not recomputed here.
      setServerTotals({
        addOnsTotal: data.addOnsTotal != null ? Number(data.addOnsTotal) : null,
        newTotal: data.newTotal != null ? Number(data.newTotal) : null,
      });
      onTotalsChanged?.({
        addOnsTotal: data.addOnsTotal != null ? Number(data.addOnsTotal) : null,
        newTotal: data.newTotal != null ? Number(data.newTotal) : null,
      });
      toast.success(removing ? a.title + ' removed from your package.' : a.title + ' added to your package.');
    } catch (e) {
      toast.error(e.message || 'Could not update your package right now.');
    } finally {
      setBusyKey(null);
    }
  };

  // EMPTY MEANS RENDER NOTHING — not an empty heading, not a "nothing here"
  // card. Silence is correct on a signature page.
  if (cards.length === 0) return null;

  const offeredCount = rows.length;
  const chips = rows.slice(0, 3);
  const moreCount = Math.max(0, offeredCount - chips.length);

  return (
    <div
      className="mt-6 rounded-[22px] p-5 sm:p-6"
      style={{ background: 'linear-gradient(180deg, #0B1224 0%, #070B18 100%)', border: '1px solid rgba(255,255,255,0.12)' }}
      data-testid="contract-upgrades"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 text-left"
        data-testid="contract-upgrades-toggle"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 shrink-0" style={{ color: '#D4AF37' }} />
            <h3
              className="text-white"
              style={{ fontFamily: 'ui-serif, Georgia, serif', fontSize: '20px', letterSpacing: '0.005em' }}
              data-testid="contract-upgrades-title"
            >
              One last touch before you sign
            </h3>
          </div>
          {/* THE REAL COUNT — what this client is offered, never a catalog number. */}
          <p className="text-[12.5px] text-white/45 mt-1.5" data-testid="contract-upgrades-count">
            {addedItems.length > 0
              ? `${addedItems.length} extra${addedItems.length === 1 ? '' : 's'} added to your package`
              : `${offeredCount} extra${offeredCount === 1 ? '' : 's'} chosen for your event`}
          </p>
          {chips.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid="contract-upgrades-chips">
              {chips.map((c) => (
                <span
                  key={c.key}
                  className="inline-block max-w-[46vw] sm:max-w-[220px] truncate rounded-full px-2.5 py-1 text-[11px] text-white/60"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid={'contract-upgrade-chip-' + c.key}
                >
                  {c.title}
                </span>
              ))}
              {moreCount > 0 && (
                <span className="text-[11px] text-white/35" data-testid="contract-upgrades-chips-more">
                  +{moreCount} more
                </span>
              )}
            </div>
          )}
        </div>
        <span
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
          style={{ background: 'transparent', border: '1px solid rgba(212,175,55,0.45)', color: '#D4AF37' }}
        >
          {open ? 'Hide' : 'View'}
          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} strokeWidth={2.5} />
        </span>
      </button>

      {open && (
        <div className="mt-5 animate-upgrade-expand" data-testid="contract-upgrades-body">
          <p className="text-[12px] text-white/40 mb-4">
            These lock into your package the moment you sign, at the price shown. Your total updates
            as you choose.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {cards.map((a) => {
              const isAdded = a.key in selected;
              const isBusy = busyKey === a.key;
              return (
                <div
                  key={a.key}
                  className="upgrade-card-lift rounded-2xl px-4 py-3.5 flex flex-col"
                  style={{
                    background: isAdded ? 'rgba(212,175,55,0.05)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${isAdded ? 'rgba(212,175,55,0.38)' : 'rgba(255,255,255,0.06)'}`,
                  }}
                  data-testid={'contract-upgrade-' + a.key}
                >
                  {a.eyebrow && (
                    <p
                      className="text-[9.5px] font-bold uppercase tracking-[0.18em] truncate"
                      style={{ color: 'rgba(212,175,55,0.75)' }}
                      data-testid={'contract-upgrade-group-' + a.key}
                    >
                      {a.eyebrow}
                    </p>
                  )}
                  <p className="text-[14px] font-semibold text-white/85 break-words mt-1" data-testid={'contract-upgrade-name-' + a.key}>
                    {a.title}
                  </p>
                  {a.benefit ? (
                    <p className="text-[11.5px] text-white/40 mt-1 leading-relaxed" style={clamp2}>{a.benefit}</p>
                  ) : null}
                  <div className="mt-3 pt-1 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => toggleItem(a)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-2 sm:py-1.5 transition-colors duration-200 disabled:opacity-50"
                      style={isAdded
                        ? { background: 'transparent', border: '1px solid rgba(212,175,55,0.6)', color: '#E7C96A' }
                        : { background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.28)', color: '#E7C96A' }}
                      data-testid={(isAdded ? 'contract-upgrade-remove-' : 'contract-upgrade-add-') + a.key}
                    >
                      {isBusy
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : (isAdded ? <Check className="w-3 h-3" strokeWidth={3} /> : <Plus className="w-3 h-3" />)}
                      {isAdded ? 'Added' : 'Add to My Package'}
                    </button>
                    <span className="text-[12px] font-semibold text-[#E7C96A] tabular-nums text-right shrink-0">
                      +{money(a.amount)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Running total — the SERVER's figure, the same one the signature
              panel signs. Nothing is added up in the browser. */}
          <div
            className="sticky bottom-0 -mx-5 sm:-mx-6 mt-4 px-5 sm:px-6 py-3"
            style={{
              background: 'linear-gradient(180deg, rgba(7,11,24,0.82) 0%, #070B18 60%)',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(8px)',
            }}
            data-testid="contract-upgrades-total"
          >
            <div className="flex items-center justify-between text-[12px] text-white/55">
              <span>Extras added</span>
              <span className="font-semibold tabular-nums" style={{ color: '#E7C96A' }} data-testid="contract-upgrades-addons-total">
                {addOnsTotal != null ? `+${money(addOnsTotal)}` : '—'}
              </span>
            </div>
            {hasBase && (
              <div className="flex items-center justify-between text-[14px] font-bold text-white mt-1">
                <span>Your total</span>
                <span className="tabular-nums" data-testid="contract-upgrades-newtotal">
                  {totalsResolved ? money(newTotal) : 'Confirming with our office…'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
